// src/components/FormSubmissionView.jsx — Fiches d'arrivée LOT 1 (ADR-188)
//
// Rendu admin STRICT d'un payload de soumission de formulaire voyageur.
// Le payload est une donnée 100 % non-fiable (saisie voyageur, clés/valeurs
// arbitraires). Règle de sécurité absolue (durcissement ADR-188) :
//   - JAMAIS d'injection HTML brute — tout passe par les enfants texte
//     React, donc échappé par le moteur de rendu.
//   - Chaque clé ET chaque valeur est coercée en String avant rendu :
//     {String(key)} / {String(value)}. Le markup éventuel (<script>, <img …>)
//     est affiché ÉCHAPPÉ et visible, jamais interprété.
//   - Les valeurs non-string sont sérialisées proprement (arrays → join ', ',
//     objets → JSON.stringify), jamais [object Object] ni rendu d'enfant objet.
//
// Composant volontairement autonome (PAS d'intégration ResaV2Modal — Lot 3).

// Sérialise une valeur de payload en chaîne lisible et SÛRE.
// - string / number / boolean → String(...)
// - null / undefined → '—'
// - array (ex. cases à cocher) → 'a, b, c' (chaque élément stringifié)
// - objet → JSON.stringify (fallback lisible, jamais [object Object])
export function formatSubmissionValue(value) {
  if (value === null || value === undefined) return "—"
  // Glose admin de la sentinelle « après 23h » (M4, Q-APRES-23H) : la valeur
  // est transmise TELLE QUELLE par le formulaire (jamais un faux 23h30) — à
  // l'affichage on la dit en clair, sans inventer d'heure.
  if (value === "apres_23h") return "après 23h (arrivée tardive — heure exacte non fixée)"
  if (Array.isArray(value)) {
    return value.map((v) => formatSubmissionValue(v)).join(", ")
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (_e) {
      // Référence circulaire ou objet non-sérialisable → fallback texte brut.
      return String(value)
    }
  }
  return String(value)
}

// Résout le libellé affiché pour une clé de payload.
// `fields` (optionnel) = tableau des champs du formulaire (shape
// { reference, label }). Si une référence correspond à la clé → on affiche le
// label métier. Sinon (clé inconnue / champ supprimé) → la clé brute, échappée.
function resolveLabel(key, fieldsByRef) {
  const field = fieldsByRef[key]
  if (field && field.label) return String(field.label)
  return String(key)
}

export default function FormSubmissionView({ payload, fields = [], emptyLabel = "Soumission vide." }) {
  const safePayload = payload && typeof payload === "object" && !Array.isArray(payload) ? payload : {}
  const entries = Object.entries(safePayload)

  // Index reference → field, pour résoudre les libellés métier quand dispo.
  const fieldsByRef = {}
  if (Array.isArray(fields)) {
    for (const f of fields) {
      if (f && typeof f.reference === "string" && f.reference) fieldsByRef[f.reference] = f
    }
  }

  if (entries.length === 0) {
    return (
      <div data-testid="form-submission-view" style={{ fontSize: 12, color: "#999", fontStyle: "italic" }}>
        {String(emptyLabel)}
      </div>
    )
  }

  return (
    <dl
      data-testid="form-submission-view"
      style={{ margin: 0, display: "grid", gridTemplateColumns: "minmax(120px, 220px) 1fr", gap: "4px 12px", fontSize: 12 }}
    >
      {entries.map(([key, value]) => (
        <div key={String(key)} style={{ display: "contents" }}>
          <dt style={{ color: "#666", fontWeight: 600, wordBreak: "break-word" }}>
            {resolveLabel(key, fieldsByRef)}
          </dt>
          <dd style={{ margin: 0, color: "#222", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {formatSubmissionValue(value)}
          </dd>
        </div>
      ))}
    </dl>
  )
}
